import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import { EMPTY_ARRAY_LENGTH } from '../../../../aumico-lib-core-fe/src/lib';
import { TaskState } from '../../../../aumico-lib-cpmm-fe/src/lib/enums/task-state.enum';
import { ProjectsOverviewActions } from '../actions/projects-overview.actions';
import { IssueDetailDto } from '../dtos/issue-detail.dto.interface';
import { ProjectMetaInformationDto } from '../dtos/project-meta-information-dto.interface';
import { WorkflowDataDto } from '../dtos/workflow-data.dto.interface';
const debug = Debug('aumico:projects:ProjectsOverviewState');

export interface BulkDeleteDependencyWarning {
    projectId: string;
    dependentReports: string[];
}

export interface ProjectsOverviewState extends EntityState<ProjectMetaInformationDto> {
    activeProjectId: string;
    totalCount: number;
    loading: boolean;
    error: string | null;
    pendingDependencyWarnings: BulkDeleteDependencyWarning[];
}

// Entity adapter
export const projectsMetadataAdapter = createEntityAdapter<ProjectMetaInformationDto>({
    selectId: (project) => project.projectId,
});

// Initial state
export const initialProjectsOverviewState: ProjectsOverviewState = projectsMetadataAdapter.getInitialState({
    activeProjectId: undefined,
    totalCount: 0,
    loading: false,
    error: null,
    pendingDependencyWarnings: [],
});

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(n, max));

export const projectsOverviewReducer = createReducer(
    initialProjectsOverviewState,

    // Load Projects
    on(ProjectsOverviewActions.loadProjectsOverview, (state) => ({ ...state, loading: true })),
    on(ProjectsOverviewActions.projectsOverviewLoaded, (state, { response }) => {
        debug('Projects overview loaded:', response);
        return projectsMetadataAdapter.setAll(response.projectsMetaInformation, {
            ...state,
            totalCount: response.totalCount,
            loading: false,
            error: null,
        });
    }),
    on(ProjectsOverviewActions.projectsOverviewLoadFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Load More Projects
    on(ProjectsOverviewActions.loadMoreProjects, (state) => ({ ...state, loading: true })),
    on(ProjectsOverviewActions.moreProjectsLoaded, (state, { response }) => {
        return projectsMetadataAdapter.addMany(response.projectsMetaInformation, {
            ...state,
            totalCount: response.totalCount,
            loading: false,
            error: null,
        });
    }),
    on(ProjectsOverviewActions.moreProjectsLoadFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Update Project Status
    on(ProjectsOverviewActions.updateProjectStatus, (state) => ({ ...state, loading: true })),
    on(ProjectsOverviewActions.projectStatusUpdated, (state, { response }) => {
        return projectsMetadataAdapter.updateOne(
            { id: response.projectId, changes: response },
            {
                ...state,
                loading: false,
                error: null,
            },
        );
    }),
    on(ProjectsOverviewActions.projectStatusUpdateFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Bulk Update Project Status
    on(ProjectsOverviewActions.bulkUpdateProjectStatus, (state) => ({ ...state, loading: true })),
    on(ProjectsOverviewActions.bulkProjectStatusUpdated, (state, { response }) => {
        // Create update objects for each project in the response
        const updates = response.map(project => ({
            id: project.projectId,
            changes: project
        }));

        return projectsMetadataAdapter.updateMany(updates, {
            ...state,
            loading: false,
            error: null,
        });
    }),
    on(ProjectsOverviewActions.bulkUpdateProjectStatusFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Delete Project
    on(ProjectsOverviewActions.deleteProject, (state) => ({ ...state, loading: true })),
    on(ProjectsOverviewActions.projectDeleted, (state, { response }) => {
        return projectsMetadataAdapter.removeOne(response, {
            ...state,
            loading: false,
            error: null,
        });
    }),
    on(ProjectsOverviewActions.projectDeleteFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Bulk Delete Projects
    on(ProjectsOverviewActions.bulkDeleteProjects, (state) => ({ ...state, loading: true })),
    on(ProjectsOverviewActions.bulkProjectsDeleted, (state, { deletedIds, warnings }) => {
        debug('Bulk projects deleted:', deletedIds, 'warnings:', warnings);
        const safeDeletedIds = Array.isArray(deletedIds) ? deletedIds : [];
        const safeWarnings = Array.isArray(warnings) ? warnings : [];

        return projectsMetadataAdapter.removeMany(safeDeletedIds, {
            ...state,
            totalCount: Math.max(0, state.totalCount - safeDeletedIds.length),
            loading: false,
            error: null,
            pendingDependencyWarnings: safeWarnings,
        });
    }),
    on(ProjectsOverviewActions.bulkProjectsDeleteFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),
    on(ProjectsOverviewActions.clearBulkDeleteWarnings, (state) => ({
        ...state,
        pendingDependencyWarnings: [],
    })),

    // Set Active Project
    on(ProjectsOverviewActions.setActiveProjectId, (state, { projectId }) => ({
        ...state,
        activeProjectId: projectId,
    })),

    // Clear Active Project
    on(ProjectsOverviewActions.clearActiveProjectId, (state) => ({
        ...state,
        activeProjectId: undefined,
    })),

    // Load specific Project Data by Annual Report Meta Info Id
    on(ProjectsOverviewActions.fetchProjectDataByProjectId, (state) => ({ ...state, loading: true })),
    on(ProjectsOverviewActions.projectDataByProjectIdFetched, (state, { projectId, projectMetaData }) => {
        return projectsMetadataAdapter.upsertOne(projectMetaData, {
            ...state,
            activeProjectId: projectId,
            loading: false,
            error: null,
        });
    }),
    on(ProjectsOverviewActions.projectDataByProjectIdFetchFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.message,
    })),

    // Reset state
    on(ProjectsOverviewActions.resetProjectsOverviewState, (state) =>
        projectsMetadataAdapter.removeAll({
            ...state,
            activeProjectId: undefined,
            totalCount: 0,
            loading: false,
        }),
    ),

    on(ProjectsOverviewActions.updateProjectWorkflowdataForTask, (state, { projectId, taskId, task }) => {
        const project = state.entities[projectId];
        if (!project) return state;

        const workflowDataArray = project.workflowData;
        if (!workflowDataArray || workflowDataArray.length === EMPTY_ARRAY_LENGTH) return state;

        // Find the workflow data item that contains the task
        let workflowIndex = -1;
        let issueIndex = -1;
        let targetWorkflow: WorkflowDataDto | null = null;

        for (let wfIdx = 0; wfIdx < workflowDataArray.length; wfIdx++) {
            const wf = workflowDataArray[wfIdx];
            if (wf.issuesDetails?.length > 0) {
                const foundIssueIdx = wf.issuesDetails.findIndex((i) => i.issueId === taskId);
                if (foundIssueIdx !== -1) {
                    workflowIndex = wfIdx;
                    issueIndex = foundIssueIdx;
                    targetWorkflow = wf;
                    break;
                }
            }
        }

        if (workflowIndex === -1 || issueIndex === -1 || !targetWorkflow) return state;

        const prevIssue = targetWorkflow.issuesDetails[issueIndex];
        const prevState = prevIssue.state;
        const newState = task.taskState;

        // Count only transitions crossing the DONE boundary
        let delta = 0;
        if (prevState !== TaskState.DONE && newState === TaskState.DONE) delta = +1;
        else if (prevState === TaskState.DONE && newState !== TaskState.DONE) delta = -1;

        // Update the one issue immutably
        const updatedIssues: IssueDetailDto[] = [...targetWorkflow.issuesDetails];
        updatedIssues[issueIndex] = { ...prevIssue, state: newState };

        // Recalculate completed (clamped to [0, total])
        const total = targetWorkflow.totalIssueCount ?? updatedIssues.length;
        const completedRaw = (targetWorkflow.completedIssueCount ?? 0) + delta;
        const completed = Math.max(0, Math.min(completedRaw, total));

        const updatedWorkflow: WorkflowDataDto = {
            ...targetWorkflow,
            issuesDetails: updatedIssues,
            completedIssueCount: completed,
        };

        // Update the workflow data array
        const updatedWorkflowDataArray = [...workflowDataArray];
        updatedWorkflowDataArray[workflowIndex] = updatedWorkflow;

        return projectsMetadataAdapter.updateOne(
            {
                id: projectId,
                changes: {
                    workflowData: updatedWorkflowDataArray,
                },
            },
            state,
        );
    }),

    on(ProjectsOverviewActions.updateWorkflowdataForAllTasksBySectionId, (state, { projectId, sectionId, tasks }) => {
        const project = state.entities[projectId];
        if (!project) return state;

        const workflowDataArray = project.workflowData;
        if (!workflowDataArray || workflowDataArray.length === EMPTY_ARRAY_LENGTH) return state;

        // Find the workflow data item that contains the section
        let workflowIndex = -1;
        let sectionIndex = -1;
        let targetWorkflow: WorkflowDataDto | null = null;

        for (let wfIdx = 0; wfIdx < workflowDataArray.length; wfIdx++) {
            const wf = workflowDataArray[wfIdx];
            if (wf.issuesDetails?.length > 0) {
                const foundSectionIdx = wf.issuesDetails.findIndex((i) => i.issueId === sectionId);
                if (foundSectionIdx !== -1) {
                    workflowIndex = wfIdx;
                    sectionIndex = foundSectionIdx;
                    targetWorkflow = wf;
                    break;
                }
            }
        }

        if (workflowIndex === -1 || sectionIndex === -1 || !targetWorkflow) return state;

        const sectionIssue = targetWorkflow.issuesDetails[sectionIndex];

        // Consider only tasks belonging to this sectionId
        const sectionTasks = (tasks ?? []).filter((t) => t.sectionId === sectionId);

        // If no tasks provided for this section, treat as not done (OPEN)
        const allDone = sectionTasks.length > 0 && sectionTasks.every((t) => t.taskState === TaskState.DONE);

        const prevState = sectionIssue.state;
        const nextState = allDone ? TaskState.DONE : TaskState.OPEN;

        // Delta only when crossing DONE boundary
        let delta = 0;
        if (prevState !== TaskState.DONE && nextState === TaskState.DONE) delta = +1;
        else if (prevState === TaskState.DONE && nextState !== TaskState.DONE) delta = -1;

        // Update the section issue immutably
        const updatedIssues: IssueDetailDto[] = [...targetWorkflow.issuesDetails];
        updatedIssues[sectionIndex] = { ...sectionIssue, state: nextState };

        const total = targetWorkflow.totalIssueCount ?? updatedIssues.length;
        const completed = clamp((targetWorkflow.completedIssueCount ?? 0) + delta, 0, total);

        const updatedWorkflow: WorkflowDataDto = {
            ...targetWorkflow,
            issuesDetails: updatedIssues,
            completedIssueCount: completed,
        };

        // Update the workflow data array
        const updatedWorkflowDataArray = [...workflowDataArray];
        updatedWorkflowDataArray[workflowIndex] = updatedWorkflow;

        return projectsMetadataAdapter.updateOne(
            {
                id: projectId,
                changes: {
                    workflowData: updatedWorkflowDataArray,
                },
            },
            state,
        );
    }),
);

export const projectsOverviewFeature = createFeature({
    name: 'aumicoProjectsOverviewFeature',
    reducer: projectsOverviewReducer,
});

export const { selectAumicoProjectsOverviewFeatureState, selectTotalCount, selectLoading, selectError } =
    projectsOverviewFeature;

const { selectAll, selectEntities } = projectsMetadataAdapter.getSelectors();

export const selectProjectsOverview = createSelector(selectAumicoProjectsOverviewFeatureState, selectAll);

export const selectProjectsOverviewEntities = createSelector(selectAumicoProjectsOverviewFeatureState, selectEntities);


export const selectActiveProjectId = createSelector(
    selectAumicoProjectsOverviewFeatureState,
    (state) => state.activeProjectId,
);

export const selectActiveProject = createSelector(
    selectProjectsOverviewEntities,
    selectActiveProjectId,
    (entities, projectId) => entities[projectId],
);

export const selectPendingDependencyWarnings = createSelector(
    selectAumicoProjectsOverviewFeatureState,
    (state) => state.pendingDependencyWarnings,
);
